import { toast } from "sonner";
import {
  Waves,
  Share2,
  Timer,
  CalendarCheck,
  Flame,
  Trophy,
  Repeat,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { GlassCard } from "@/components/GlassCard";
import { useLanguage } from "@/i18n/LanguageContext";
import { pickMessage } from "@/lib/messages";

const RecapStat = ({ icon: Icon, label, value, testId }) => (
  <div className="flex items-center gap-2.5">
    <div className="h-8 w-8 shrink-0 rounded-lg bg-cyan-300/10 border border-cyan-300/20 flex items-center justify-center">
      <Icon size={14} className="text-cyan-300/90" />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
        {label}
      </p>
      <p
        data-testid={testId}
        className="text-sm font-semibold truncate [font-variant-numeric:tabular-nums]"
      >
        {value}
      </p>
    </div>
  </div>
);

/**
 * Stage 2.2: branded in-app weekly Motion Card recap.
 * Share/Copy uses navigator.share when available, otherwise clipboard —
 * no external social APIs, no login required.
 */
export const WeeklyRecapCard = ({ stats, loading }) => {
  const { t, locale } = useLanguage();

  if (loading || !stats) {
    return <Skeleton className="h-64 rounded-2xl bg-white/10" />;
  }

  const hasData = stats.activities_this_week > 0;
  const streakText = `${stats.current_streak} ${
    stats.current_streak === 1 ? t("common.day") : t("common.days")
  }`;
  const bestDayText = stats.best_day
    ? `${new Date(`${stats.best_day.date}T00:00:00`).toLocaleDateString(locale, {
        weekday: "long",
      })} · ${stats.best_day.minutes} ${t("common.min")}`
    : "\u2014";
  const mostFrequentText = stats.most_frequent_type
    ? t(`types.${stats.most_frequent_type}`)
    : "\u2014";
  const message = pickMessage(t("recap.pool"), stats.activities_this_week);

  const canNativeShare = typeof navigator !== "undefined" && Boolean(navigator.share);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("recap.copied"));
    } catch {
      toast.error(t("recap.copyError"));
    }
  };

  const handleShare = async () => {
    const text = t("recap.summaryTemplate", {
      minutes: stats.weekly_minutes,
      count: stats.activities_this_week,
      streak: streakText,
      bestDay: bestDayText,
      type: mostFrequentText,
      pct: stats.weekly_progress_pct,
      message,
    });
    if (canNativeShare) {
      try {
        await navigator.share({ text });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // user closed the share sheet
      }
    }
    await copyToClipboard(text);
  };

  return (
    <GlassCard glow className="p-5" data-testid="weekly-recap-card">
      {/* Brand header — never translated */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Waves size={14} className="text-cyan-300" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-cyan-300/90 font-semibold">
            Blue Ocean Motion
          </span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
          {t("recap.kicker")}
        </span>
      </div>

      <h2 className="mt-3 text-lg font-semibold tracking-tight">{t("recap.title")}</h2>

      {hasData ? (
        <>
          <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4">
            <RecapStat
              icon={Timer}
              label={t("recap.totalMinutes")}
              value={`${stats.weekly_minutes} ${t("common.min")}`}
              testId="recap-minutes"
            />
            <RecapStat
              icon={CalendarCheck}
              label={t("recap.activities")}
              value={stats.activities_this_week}
              testId="recap-activities"
            />
            <RecapStat
              icon={Flame}
              label={t("recap.streak")}
              value={streakText}
              testId="recap-streak"
            />
            <RecapStat
              icon={Trophy}
              label={t("recap.bestDay")}
              value={bestDayText}
              testId="recap-best-day"
            />
            <RecapStat
              icon={Repeat}
              label={t("recap.mostFrequent")}
              value={mostFrequentText}
              testId="recap-most-frequent"
            />
            <RecapStat
              icon={Target}
              label={t("recap.goalProgress")}
              value={`${stats.weekly_progress_pct}%`}
              testId="recap-goal-pct"
            />
          </div>

          <div className="mt-4">
            <Progress value={stats.weekly_progress_pct} className="h-1.5 bg-white/10" />
          </div>

          <p
            data-testid="recap-message"
            className="motion-message mt-4 text-sm leading-relaxed font-medium"
          >
            {message}
          </p>

          <Button
            data-testid="recap-share-button"
            onClick={handleShare}
            variant="outline"
            className="mt-4 w-full rounded-xl bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white active:scale-[0.98]"
          >
            <Share2 size={15} className="mr-1.5" />
            {canNativeShare ? t("recap.shareSummary") : t("recap.copySummary")}
          </Button>
        </>
      ) : (
        <p
          data-testid="recap-empty"
          className="mt-3 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed"
        >
          {t("recap.empty")}
        </p>
      )}
    </GlassCard>
  );
};
